<template lang="pug">
  v-navigation-drawer.navigation-drawer(
    v-if="isRenderDrawerMenu"
    :value="value",
    v-bind="$attrs",
    v-on="$listeners",
    fixed,
    right,
    temporary,
    :expand-on-hover="true"
    light,
    ref="drawer"
    aria-label="Mobile menu"
  )
    .navigation-drawer__menu(ref="menu")
      .navigation-drawer__menu-profile.navigation-drawer-menu-profile(v-if="isLoggedIn")
        router-link.menu-item.navigation-drawer-menu-profile__item-user-link(
          :to="{ name: 'account' }"
        )
          UserPicture.small-avatar.navigation-drawer-menu-profile__avatar
          span.navigation-drawer-menu-profile__item-user-name {{ profileFullName }}
        NavigationDrawerMenuItem.navigation-drawer-menu-profile__item(
          key="account"
          name="Accounts"
          path="/profile/accounts"
          slug="accounts"
          :show-icon="false"
        )
        NavigationDrawerMenuItem.navigation-drawer-menu-profile__item(
          key="beneficiary"
          name="Beneficiaries"
          path="/profile/beneficiary"
          slug="beneficiary"
          :show-icon="false"
        )
        NavigationDrawerMenuItem.navigation-drawer-menu-profile__item(
          key="my-advisors"
          name="My Advisors"
          path="/profile/my-advisors"
          slug="my-advisors"
          :show-icon="false"
        )
        NavigationDrawerMenuItem.navigation-drawer-menu-profile__item(
          key="notifications"
          name="Notifications"
          path="/profile/notifications"
          slug="notifications"
          :show-icon="false"
        )
          BaseTag.thumbnail-feature-post__label(
            v-show="notificationsAllNew > 0"
            slot="name-info"
            background="#F7D2CF"
            round
          ) {{ notificationsAllNew }}
        NavigationDrawerMenuItem.navigation-drawer-menu-profile__item(
          key="settings"
          name="Settings"
          path="/profile/settings"
          slug="settings"
          :show-icon="false"
        )
      NavigationDrawerMenuItem(
        v-for="item in headerMenuChildren"
        :key="item.slug"
        v-if="visibleMenuList.includes(item.slug)"
        v-bind="item"
      )
      .navigation-drawer__buttons-row(:class="{'is--logged-in': isLoggedIn}")
        BaseButton.navigation-drawer__buttons-row-btn(
          v-if="!isLoggedIn"
          aria-label="Click on Log In link"
          color="outlined"
          @click.prevent="onLoginClick"
        ) Log In
        BaseButton.navigation-drawer__buttons-row-btn(
          v-if="!isLoggedIn"
          color="primary",
          tag="router-link"
          to="/sign-up",
          aria-label="Click on Sign Up link"
        ) Sign Up
        BaseButton.navigation-drawer__buttons-row-btn(
          v-if="isLoggedIn"
          color="outlined",
          @click="logout",
          aria-label="Click on Logout link"
        ) Logout
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { VNavigationDrawer } from 'vuetify/lib';
import { HEADER_MENU, PROFILE_MENU } from 'Config/menu';
import UserPicture from 'Components/UserPicture/UserPicture';
import { openModalLogin } from 'Components/ModalLogin';
import { scrollToTop } from 'Helpers/utils/view';
import BaseButton from 'Components/Base/BaseButton';
import BaseTag from 'Components/Base/BaseTag';
import NavigationDrawerMenuItem from './NavigationDrawerMenuItem';

export default {
  name: 'NavigationDrawerMenu',
  components: {
    VNavigationDrawer,
    NavigationDrawerMenuItem,
    UserPicture,
    BaseButton,
    BaseTag,
  },
  props: {
    value: Boolean,
  },
  data() {
    return {
      isRenderDrawerMenu: false,
    };
  },
  computed: {
    ...mapGetters('login', [
      'isLoggedIn',
    ]),
    ...mapGetters('notifications', [
      'notificationsAllNew',
    ]),
    ...mapGetters('profile', [
      'profileModel',
      'profileFullName',
    ]),
    isOpenMenu() {
      return this.$refs.drawer ? this.$refs.drawer.showOverlay : this.value;
    },
    userFullName() {
      return `${this.profileModel.first_name} ${this.profileModel.last_name}`;
    },
    visibleMenuList() {
      if (this.isLoggedIn) return ['investments', 'portfolio', 'resources'];
      return ['investments', 'farm-owners', 'advisors', 'resources'];
    },
  },
  watch: {
    value(value) {
      if (value) {
        this.isRenderDrawerMenu = true;
      }
    },
    isOpenMenu() {
      const { body } = document;
      if (this.isOpenMenu) {
        body.classList.add('is-mobile-menu-open');
      } else {
        body.classList.remove('is-mobile-menu-open');
        this.$emit('closed-menu');
        if (this.$refs.drawer && this.$refs.drawer.$el.children[0]) scrollToTop(0, this.$refs.drawer.$el.children[0]);
      }
    },
  },
  mounted() {
    // set timeout to render drawer menu
    setTimeout(() => {
      this.isRenderDrawerMenu = true;
    }, 2000);
  },
  methods: {
    ...mapActions('login', [
      'logout',
    ]),
    onLoginClick() {
      openModalLogin();
    },
  },
  staticData: {
    profileMenu: PROFILE_MENU,
    headerMenuChildren: HEADER_MENU,
  },
};
</script>

<style lang="sass" scoped>
@import '~Styles/_variables'
.navigation-drawer
  padding-top: 64px !important
  height: 100% !important
  overflow: hidden
  pointer-events: none
  z-index: 9
  background-color: $white !important
  ::v-deep .v-navigation-drawer__content
    width: 100%
    -webkit-overflow-scrolling: touch
    pointer-events: auto
  &__menu
    display: flex
    flex-direction: column
    overflow: hidden
    padding: 0 40px
    height: auto
    -webkit-overflow-scroll: touch
  &.v-navigation-drawer--open
    width: 284px !important
  &__buttons-row
    width: 100%
    padding-top: 20px
    padding-bottom: 20px
    display: flex
    flex-direction: row
    justify-content: space-between
    align-items: center
    &.is--logged-in
      justify-content: center
      .navigation-drawer__buttons-row-btn
        width: 100%
        margin-left: 0
  &__buttons-row-btn
    width: calc(50% - 7px)
  ::v-deep .v-navigation-drawer__border
    display: none
.navigation-drawer-menu-profile
  position: relative
  z-index: 1
  padding-bottom: 8px
  &:after
    content: ''
    position: absolute
    right: 0
    bottom: 0
    width: 100%
    height: 2px
    border-bottom: 1px solid $mercury
  &__avatar
    width: 20px
    height: 20px
    position: absolute
    left: -28px
    top: 21px
    font-size: 14px
    ::v-deep .img
      background-size: cover
      width: 100%
      height: 100%
    &:not(.is_image)
      box-shadow: 0px 1px 15px rgba(0, 0, 0, 0.05)
      ::v-deep .img
        background-size: contain
        width: 8px
        height: 8px
        transform: none
  &__item
    ::v-deep .mobile-item-link__link
      +font(400)
      padding: 12px 0
      padding-left: 20px
    &:after
      border-bottom: none
  &__item-user-link
    display: flex
    flex-direction: row
    align-items: center
    padding-top: 20px
    padding-bottom: 8px
  &__item-user-name
    font-size: 16px
    line-height: 22px
    +font(500)
</style>
